<template>
  <LayoutWrapper>
    <div
      class="prose prose-neutral flex max-w-none flex-col bg-light-0 dark:prose-invert dark:bg-dark-0 md:flex-row"
    >
      <div
        class="h-screen overflow-y-auto bg-light-0 px-8 dark:bg-dark-0 md:grow md:basis-0"
      >
        <OrderOverview
          v-model:selected-order="selectedOrder"
          :closed-orders="closedOrders"
          :grouped-open-orders="groupedOpenOrders"
          :is-loading="false"
          :last-updated-timestamp="lastUpdatedTimestamp"
          @refresh-orders="loadOnboardingFixtures"
        />
      </div>
      <div
        class="fixed -bottom-40 z-10 flex w-full justify-center rounded-3xl bg-light-1 align-middle transition-all dark:bg-dark-1 md:relative md:bottom-0 md:right-0 md:mt-0 md:rounded-none"
        :class="[selectedOrder ? 'h-screen w-full md:w-1/2' : 'h-0  md:w-0']"
      >
        <OrderDetails
          v-if="selectedOrder"
          v-model:selected-order="selectedOrder"
          @go-to-order="goToOrder($event)"
        />
      </div>
    </div>
  </LayoutWrapper>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import OrderOverview from '@/components/OrderManagement/OrderOverview.vue';
import OrderDetails from '@/components/OrderManagement/OrderDetails.vue';
import { useAppStore } from '@/store/app/AppStore';
import { WebEvents } from '@/native/plugins/WebEvents';
import LayoutWrapper from '@/components/LayoutWrapper.vue';
import { useLayoutStore, WorkMode } from '@/store/layout/layoutStore';
import { Order } from '@/models/Order';
import { useOrderStore } from '@/store/order/orderStore';
import assignedOrdersJson from '@/utils/sandbox/orders.json';
import businessEntityJson from '@/utils/sandbox/businessEntityOnboarding.json';
import { plainToInstance } from 'class-transformer';
import classTransformer from '@/utils/transformers/classTransformer';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useOrderOverview } from '@/composables/useOrderOverview';
import router from '@/router';

const appStore = useAppStore();
const layoutStore = useLayoutStore();
const orderStore = useOrderStore();

const {
  selectedOrder,
  lastUpdatedTimestamp,
  closedOrders,
  groupedOpenOrders,
  updateTimestamp,
  autoSelectFirstOrder,
  cleanUpOldData,
} = useOrderOverview();

const loadOnboardingFixtures = async () => {
  updateTimestamp();

  const orders = assignedOrdersJson.map((order) => {
    return plainToInstance(Order, order);
  });
  useOrderStore().setOrders(orders);

  autoSelectFirstOrder();

  await cleanUpOldData();
};

const goToOrder = async () => {
  if (!selectedOrder.value) return;

  orderStore.setOrders([selectedOrder.value]);
  useLayoutStore().setWorkMode(WorkMode.SANDBOX);

  const businessEntityMap =
    classTransformer.plainToClassEntity(businessEntityJson);

  useEntitiesStore().setBusinessEntityList(businessEntityMap);
  useEntitiesStore().setBusinessEntityMap(businessEntityMap);
  useEntitiesStore().setActiveBusinessEntityById(
    selectedOrder.value.businessEntityId
  );

  const startedOrder = await orderStore.startOrder(selectedOrder.value.id);
  if (!startedOrder) {
    console.error('Failed to start order in OnboardingSandbox', {
      orderId: selectedOrder.value.id,
    });
    return;
  }

  router.push('sandboxMode/gebaeudeuebersicht');
};

onMounted(async () => {
  document.body.classList.add('sandbox-theme');
  WebEvents.onLoggedIn();
  appStore.shouldOpenChangelogDialog().then((shouldOpen) => {
    if (shouldOpen) {
      appStore.toggleChangelogDialog();
    }
  });

  try {
    await loadOnboardingFixtures();
  } catch (e) {
    layoutStore.setLoadingState(false);
  }
});
</script>
